import { DocumentNode } from 'graphql';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UpdateUserDataDocument, User } from 'src/graphql/generated/graphql';
import {
  BottomNavigation,
  Button,
  Controller,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';

type Form = {
  note: string;
};

type AddUserNoteFormProps = {
  user: User;
  note: string;
  refetchQueries?: DocumentNode[];
  onClose: () => void;
};

export const AddUserNoteForm = ({
  user,
  note,
  refetchQueries = [],
  onClose,
}: AddUserNoteFormProps) => {
  const { t } = useTranslation();

  const { control, handleSubmit } = useForm<Form>({
    defaultValues: {
      note,
    },
  });

  const [updateUser, { loading: loadingUpdateUser }] = useMutation(
    UpdateUserDataDocument,
    {
      refetchQueries,
    },
  );

  const onSubmit = async (data: Form) => {
    await updateUser({
      variables: {
        id: user.id,
        data: {
          adminNote: data.note,
        },
      },
    });

    onClose();
  };

  const onCancel = () => {
    onClose();
  };

  return (
    <PageModal open onClose={onCancel} title={t('common.add-note')}>
      <div className="w-full min-w-small">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('common.add-note')}
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
          <BottomNavigation>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loadingUpdateUser}
            >
              {t('common.save')}
            </Button>
            <Button variant="outlined" onClick={onCancel}>
              {t('common.close')}
            </Button>
          </BottomNavigation>
        </form>
      </div>
    </PageModal>
  );
};
